import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { LocaleSwitcher } from "../i18n/LocaleSwitcher";
import { GlobalSearch } from "./GlobalSearch";
import { PortalToolsToggle } from "./PortalToolsToggle";
import { NotificationBell } from "./NotificationBell";
import { AssistantChat } from "./assistant/AssistantChat";
import TwoFactorGraceBanner from "./TwoFactorGraceBanner";
import { tmApi } from "../api/tm";
import { displayCompanyName } from "../utils/companyName";
import Ic from "./icons.jsx";
import type { ComponentType, ReactNode } from "react";

type NavEntry = {
  to: string;
  label: string;
  icon: ComponentType<Record<string, unknown>>;
  permission?: string;
  /** Force-hide regardless of permission (e.g. a customer with Forecasts disabled). */
  hidden?: boolean;
  anyOf?: string[];
  role?: string;
  /** Show only if the caller holds at least one of these roles. */
  anyOfRoles?: string[];
  /** Require exact match for active state. Use for parents like /tm whose
   *  prefix would otherwise highlight every child route alongside the leaf. */
  end?: boolean;
  /** Optional numeric badge shown next to the label (e.g. open task count). */
  badge?: number;
};

type NavGroup = {
  id: string;
  title: string;
  items: NavEntry[];
};

export function AppShell() {
  const { t } = useTranslation();
  const { state, logout, hasPermission, hasRole } = useAuth();
  const navigate = useNavigate();

  // Count of open tasks assigned to me — feeds the Tasks sidebar badge.
  // Refreshed when the auth state flips and every 60 s so the badge stays
  // roughly fresh without a websocket.
  const [openTaskCount, setOpenTaskCount] = useState(0);
  useEffect(() => {
    if (state.status !== "authenticated" || !hasPermission("tm:access")) return;
    let cancelled = false;
    async function refresh() {
      if (document.hidden) return; // no background polling — see NotificationBell
      try {
        const tasks = await tmApi.mine();
        if (!cancelled) setOpenTaskCount(tasks.length);
      } catch {
        if (!cancelled) setOpenTaskCount(0);
      }
    }
    void refresh();
    const id = setInterval(refresh, 60_000);
    const onVisible = () => { void refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { cancelled = true; clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, [state.status, hasPermission]);

  // Collapsed sidebar (icon-only) — remembered across sessions.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("imp.navCollapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("imp.navCollapsed", collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  // Mobile off-canvas nav — auto-closes on every route change (a nav item was tapped).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // The desktop icon-collapse is meaningless on mobile (we use a drawer), so never apply it there.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 820px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 820px)");
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  if (state.status !== "authenticated") return null;

  const isCustomer = state.user.roles.some((r) => r.startsWith("CUST_"));

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const groups: NavGroup[] = [
    {
      id: "overview",
      title: t("nav.group.overview", "Overview"),
      items: [{ to: "/dashboard", label: t("nav.dashboard"), icon: Ic.dashboard }],
    },
    {
      id: "products",
      title: t("nav.group.products", "Products"),
      items: [
        { to: "/catalog", label: t("nav.catalog"), icon: Ic.catalog },
        { to: "/pump-selector", label: t("nav.pumpSelector"), icon: Ic.selector },
        // IMP-10: customer-facing model comparison disabled for now (admin kept). Restore by uncommenting this + the /comparison route + import in main.tsx.
        // { to: "/comparison", label: t("nav.comparison", "Model comparison"), icon: Ic.grid },
      ],
    },
    {
      id: "commercial",
      title: t("nav.group.commercial", "Commercial"),
      items: [
        // Customers landing — the same admin page, but in Commercial because
        // for Sales the company detail view IS the per-customer analytics
        // (orders, forecasts, contracts, contacts). `companies:edit` excludes
        // CUST_MANAGER cleanly so customer users keep "My company" only.
        // Sales team sits in Commercial, not Admin: it is where the sales director works, and
        // burying the one screen written for them under Admin is how it went unfound (owner,
        // 2026-10-05). markets:view already restricts it to SUPERADMIN / ADMIN / SALES_DIRECTOR,
        // so reps never see it. Labelled apart from the "Markets" overview directly below, and
        // given its own icon — two entries with one word and one glyph is what sent him to the
        // wrong screen the first time.
        { to: "/admin/markets", label: t("nav.markets", "Sales & markets"), icon: Ic.team, permission: "markets:view" },
        // The per-market overview is hidden from the menu for now (owner, 2026-10-05): it sat
        // directly under "Sales & markets" with the same audience and overlapping content.
        // The route and the page are untouched, so /markets still answers and nothing has to
        // be rebuilt to bring it back — put this line back and it returns.
        // "Companies", not "Customers": the page it opens lists prospects and
        // non-clients too, and its own heading has always said Companies.
        { to: "/admin/companies", label: t("nav.companies"), icon: Ic.factory, anyOf: ["companies:edit"] },
        { to: "/orders", label: t("nav.orders", "Orders"), icon: Ic.box, permission: "orders:view" },
        { to: "/offers", label: t("nav.offers", "Offers"), icon: Ic.pdf, permission: "offers:view" },
        { to: "/leads", label: t("leads.nav", "Leads"), icon: Ic.target, permission: "leads:view" },
        { to: "/agents", label: t("agents.nav", "Agents"), icon: Ic.handshake, permission: "agents:view" },
        { to: "/my-projects", label: t("nav.myProjects", "My Projects"), icon: Ic.briefcase, permission: "customer_projects:edit" },
        { to: "/admin/customer-projects", label: t("nav.customerProjects", "Customer Projects"), icon: Ic.briefcase, permission: "customer_projects:price" },
        // Finance sits high in Commercial — for customers it lands 3rd (Orders, My Projects, Finance).
        { to: "/finance", label: t("finance.nav", "Finance"), icon: Ic.finance, anyOf: ["finance:view_own", "finance:view_assigned", "finance:view_all"] },
        { to: "/forecasts", label: t("nav.forecasts", "Forecasts"), icon: Ic.trend, permission: "forecasts:view", hidden: isCustomer && !state.user.forecastsEnabled },
        {
          // Customers land on the read-only list; sales / admin land on the
          // richer admin page that also has the "New contract" CTA.
          to: hasPermission("contracts:create") ? "/admin/contracts" : "/contracts",
          label: isCustomer ? t("nav.contract", "Contract") : t("nav.contracts", "Contracts"),
          icon: Ic.contract,
          permission: "contracts:view",
        },
        { to: "/support", label: t("nav.support", "Support"), icon: Ic.help, permission: "support:view_own" },
        { to: "/admin/support", label: t("admin.nav.support", "Tickets"), icon: Ic.bell, anyOf: ["support:view_all", "support:view_assigned"] },
      ],
    },
    {
      id: "resources",
      title: t("nav.group.resources", "Resources"),
      items: [
        { to: "/materials", label: t("nav.materials"), icon: Ic.materials },
        { to: "/faq", label: t("nav.faq", "FAQ"), icon: Ic.help, permission: "service:view" },
        { to: "/tutorials", label: t("nav.tutorials", "Service tutorials"), icon: Ic.book, permission: "service:view" },
        { to: "/news", label: t("nav.news"), icon: Ic.news },
      ],
    },
    {
      id: "tm",
      title: t("nav.group.tm", "Task Manager"),
      items: [
        { to: "/tm",          label: t("nav.tm.overview", "Overview"), icon: Ic.dashboard, permission: "tm:access", end: true },
        { to: "/tm/tasks",    label: t("nav.tm.tasks", "Tasks"),       icon: Ic.check,    permission: "tm:access", badge: openTaskCount },
        { to: "/tm/projects", label: t("nav.tm.projects", "Projects"), icon: Ic.book,     permission: "tm:access" },
        { to: "/tm/meetings", label: t("nav.tm.meetings", "Meetings"), icon: Ic.calendar, permission: "tm:access" },
        { to: "/tm/team",     label: t("nav.tm.team", "Team"),         icon: Ic.user,     anyOf: ["tm:manage_team"] },
        { to: "/tm/activity", label: t("nav.tm.activity", "Activity"), icon: Ic.list,     permission: "tm:access" },
      ],
    },
    {
      // Personal settings for internal staff. Only one entry today ("Moj mejl",
      // the caller's own mailbox), and its label lives under mail.* because the
      // whole module is internal-only — a nav.* key would have to be translated
      // into all customer locales for no reader.
      id: "settings",
      title: t("mail.nav.group", "Settings"),
      items: [
        { to: "/settings/mail", label: t("mail.nav.settings", "My email"), icon: Ic.send, permission: "mail:connect" },
      ],
    },
    {
      id: "myCompany",
      title: t("nav.group.myCompany", "My company"),
      items: [
        { to: "/admin/users", label: t("nav.myTeam", "My team"), icon: Ic.user, role: "CUST_MANAGER" },
        {
          to: "/contact",
          label: t("nav.contact", "Contact"),
          icon: Ic.handshake,
          anyOfRoles: ["CUST_MANAGER", "CUST_PROCUREMENT", "CUST_SERVICE"],
        },
      ],
    },
    {
      id: "admin",
      title: t("nav.group.admin", "Admin"),
      items: [
        // Companies/Customers moved up to Commercial — keep this group
        // focused on admin-only surfaces (users, catalog, news, …).
        { to: "/admin/kpi", label: t("nav.kpi", "KPI targets"), icon: Ic.trend, anyOf: ["kpi:view_team", "kpi:view_all"] },
        { to: "/admin/users", label: t("nav.users"), icon: Ic.user, role: "ADMIN" },
        { to: "/admin/catalog/products", label: t("nav.adminCatalog", "Catalog"), icon: Ic.catalog, permission: "catalog:edit" },
        // The categories screen has existed, routed and permission-guarded, since September, and
        // nothing in the menu pointed at it — so the only way in was to type the URL. The owner
        // asked twice how to add a category and a picture; the answer was always "a screen you
        // cannot see" (2026-10-04).
        { to: "/admin/catalog/categories", label: t("nav.adminCategories", "Categories"), icon: Ic.list, permission: "catalog:edit" },
        { to: "/admin/catalog/spare-parts", label: t("nav.adminSpareParts", "Spare parts"), icon: Ic.tools, permission: "catalog:edit" },
        { to: "/admin/catalog/comparison", label: t("nav.adminComparison", "Comparisons"), icon: Ic.grid, permission: "catalog:edit" },
        { to: "/admin/materials", label: t("nav.adminMaterials", "Materials"), icon: Ic.materials, permission: "materials:upload" },
        { to: "/admin/news", label: t("nav.adminNews", "News"), icon: Ic.news, permission: "news:create" },
        { to: "/admin/service/faq", label: t("nav.adminFaq", "FAQ"), icon: Ic.help, permission: "service:create" },
        { to: "/admin/service/tutorials", label: t("nav.adminTutorials", "Service tutorials"), icon: Ic.book, permission: "service:create" },
        { to: "/admin/contracts", label: t("nav.adminContracts", "Contracts"), icon: Ic.contract, role: "ADMIN" },
        { to: "/admin/orders", label: t("nav.adminOrders", "Order review"), icon: Ic.box, role: "ADMIN" },
        { to: "/admin/erp", label: t("nav.adminErp", "ERP outbox"), icon: Ic.refresh, role: "ADMIN" },
        { to: "/admin/audit-log", label: t("nav.adminAudit", "Audit log"), icon: Ic.book, permission: "audit:view" },
        { to: "/admin/emails/preview", label: t("nav.adminEmails", "Email preview"), icon: Ic.send, role: "ADMIN" },
        // "Portal settings", not "Settings" — the personal group above already
        // owns that word (My email), and these are the global app switches.
        { to: "/admin/settings", label: t("nav.adminSettings", "Portal settings"), icon: Ic.cog, permission: "settings:manage" },
        { to: "/admin/etl", label: t("etl.nav", "ETL"), icon: Ic.refresh, permission: "etl:view" },
      ],
    },
  ];

  const visibleGroups = groups
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => {
        if (it.hidden) return false;
        if (it.anyOf) return it.anyOf.some((p) => hasPermission(p));
        if (it.permission) return hasPermission(it.permission);
        if (it.role) return hasRole(it.role) || hasRole("SUPERADMIN");
        if (it.anyOfRoles) return it.anyOfRoles.some((r) => hasRole(r));
        return true;
      }),
    }))
    .filter((g) => g.items.length > 0);

  const initials = `${state.user.firstName?.[0] ?? ""}${state.user.lastName?.[0] ?? ""}`.toUpperCase() || "—";
  const company = state.user.companyName !== null
    ? displayCompanyName(state.user.companyName)
    : (state.user.impInternal ? "IMP Pumps · Internal" : "");

  return (
    <div className={`app${collapsed && !isMobile ? " nav-collapsed" : ""}${mobileNavOpen ? " mobile-nav-open" : ""}`}>
      <div className="mobile-nav-backdrop" onClick={() => setMobileNavOpen(false)} aria-hidden="true" />
      <aside className="sidebar">
        <Link to="/dashboard" className="brand" style={{ textDecoration: "none" }}>
          <span className="brand-mark" style={{ overflow: "hidden", padding: 0, background: "#fff" }}>
            <img src="/assets/logo.jpg" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </span>
          <span className="brand-text">
            <b>IMP Pumps</b>
            <span>{t("app.tagline")}</span>
          </span>
        </Link>

        <button
          type="button"
          className="nav-toggle"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={t("nav.toggleMenu", "Toggle menu")}
          title={t("nav.toggleMenu", "Toggle menu")}
        >
          <Ic.arrowRight />
        </button>

        <nav className="nav">
          {visibleGroups.map((group) => (
            <div key={group.id}>
              <div className="nav-section">{group.title}</div>
              {group.items.map((it) => (
                <NavItem key={it.to} to={it.to} label={it.label} icon={<it.icon />} end={it.end} badge={it.badge} />
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-foot">
          {state.user.avatarUrl ? (
            <img
              src={state.user.avatarUrl}
              alt=""
              className="user-avatar"
              style={{ padding: 0, objectFit: "cover", border: "1px solid var(--border)" }}
            />
          ) : (
            <span className="user-avatar">{initials}</span>
          )}
          <div className="user-meta">
            <b>{state.user.firstName} {state.user.lastName}</b>
            <span>{company}</span>
          </div>
          <Link to="/profile" className="icon-btn" title={t("nav.profile")} style={{ display: "grid", placeItems: "center", padding: 6, color: "var(--ink-3)" }}>
            <Ic.user />
          </Link>
        </div>
      </aside>

      <header className="topbar">
        <button
          type="button"
          className="mobile-nav-btn"
          onClick={() => setMobileNavOpen((o) => !o)}
          aria-label={t("nav.toggleMenu", "Toggle menu")}
          aria-expanded={mobileNavOpen}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        {hasPermission("tm:access") && (
          // Toggle visible only to IMP-internal users — tm:access is the
          // existing proxy for "is internal" since the matrix already grants
          // it to every internal role and to no customer role.
          <PortalToolsToggle />
        )}
        <div className="topbar-search" style={{ flex: 1, maxWidth: 480 }}>
          <GlobalSearch />
        </div>
        <div className="topbar-actions" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
          <NotificationBell />
          <LocaleSwitcher />
          <button type="button" onClick={handleLogout} title={t("nav.logout")} style={logoutBtnStyle}>
            {t("nav.logout")}
          </button>
        </div>
      </header>

      <main className="app-main">
        <TwoFactorGraceBanner />
        <Outlet />
      </main>

      <AssistantChat />
    </div>
  );
}

function NavItem({
  to, label, icon, end, badge,
}: { to: string; label: string; icon: ReactNode; end?: boolean; badge?: number }) {
  return (
    <NavLink
      to={to}
      end={end}
      title={label}
      className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
      style={{ cursor: "pointer" }}
    >
      {icon}
      <span style={{ flex: 1 }}>{label}</span>
      {badge !== undefined && badge > 0 && (
        <span
          aria-label={`${badge} pending`}
          style={{
            background: "var(--imp-primary)",
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
            padding: "1px 7px",
            borderRadius: 999,
            minWidth: 18,
            textAlign: "center",
            lineHeight: 1.5,
          }}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}

const logoutBtnStyle: React.CSSProperties = {
  padding: "6px 12px",
  background: "transparent",
  border: "1px solid var(--border)",
  borderRadius: 6,
  color: "var(--ink-2)",
  fontSize: 13,
  cursor: "pointer",
  whiteSpace: "nowrap",
  flex: "none",
};
